import { Box } from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import Text from "../../../../../components/Text";
import ActionMenu from "../../../../../components/app/ActionMenu";
import DeleteModal from "../../../../../components/app/DeleteModal";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useArchiveState } from "../../../../../hooks/useArchiveState";
import { useContactDictionaryArchive } from "../../../../../queries/communication/dictionary/use-dicationary-archive";
import { useContactDictionaryList } from "../../../../../queries/communication/dictionary/use-dictionary-list";
import {
  DEF_PAGINATION,
  applyDefaultColumnSettings,
  getAddressFormat,
  getXtenantId,
} from "../../../../../utils/utils";
import UpsertContactDictionary from "./UpsertContactDictionary";
import {
  CONTACT_TYPE_OPTIONS_MAPPER,
  ContactDictionaryFilterState,
} from "./contact-dictionary-constant";

const ContactDictionary = () => {
  const [archive, setArchive] = useState(false);
  const { mutateAsync: archiveMutation } = useContactDictionaryArchive();
  const { deleteDisc, archiveHandler } = useArchiveState(archiveMutation);

  const [pagination, setPagination] =
    useState<GridPaginationModel>(DEF_PAGINATION);

  const { contactDictionaryDisclosure, contactDictionaryFilters } =
    useOutletContext<{
      handleContactDictionaryFilterChange: (key: string, value: string) => void;
      handleContactDictionarySearch: (query: string) => void;
      contactDictionaryDisclosure: DisclosureHost;
      contactDictionaryFilters: ContactDictionaryFilterState;
    }>();

  const {
    data: contactDictionary,
    isFetching,
    isLoading,
    refetch,
  } = useContactDictionaryList({
    pagination,
    gridSorting: [],
    archive: contactDictionaryFilters?.archive,
    contactType: (contactDictionaryFilters?.contactType as any)?.value,
    searchString: contactDictionaryFilters?.searchQuery,
    xTENANTID: getXtenantId(),
  });

  const handleActionClick = (action: string, row: any) => {
    if (action === "edit") {
      const transData = {
        ...row,
        contactType: {
          value: row?.contactType,
          label: CONTACT_TYPE_OPTIONS_MAPPER[row?.contactType],
        },
      };
      contactDictionaryDisclosure.open(transData);
    } else if (action === "archive" || action === "unarchive") {
      setArchive(row?.archive);
      deleteDisc.open(row?.uuid);
    }
  };

  const COLUMNS = applyDefaultColumnSettings([
    {
      field: "fullName",
      headerName: "Name",
      minWidth: 150,
      flex: 0.5,
      renderCell: ({ row }) => (
        <RenderWithTooltip value={row?.fullName ?? "-"} />
      ),
    },
    {
      field: "contactType",
      minWidth: 240,
      disableColumnMenu: true,
      headerName: "Contact Type",
      renderCell: ({ row }) => (
        <Text isTabletext>
          {CONTACT_TYPE_OPTIONS_MAPPER[row?.contactType] ?? "-"}
        </Text>
      ),
    },
    {
      field: "contactNumber",
      minWidth: 240,
      disableColumnMenu: true,
      headerName: "Contact",
      renderCell: ({ value }) => {
        return <RenderWithTooltip value={value} />;
      },
    },
    {
      field: "faxNumber",
      flex: 0.4,
      headerName: "Fax",
      renderCell: ({ value }) => <RenderWithTooltip value={value || "-"} />,
    },
    {
      field: "email",
      flex: 0.6,
      headerName: "Email",
      renderCell: ({ value }) => <RenderWithTooltip value={value || "-"} />,
    },
    {
      field: "address",
      flex: 0.6,
      headerName: "Address",
      renderCell: ({ value }) => (
        <RenderWithTooltip value={getAddressFormat(value) || "-"} />
      ),
    },
    {
      field: "description",
      flex: 0.6,
      headerName: "Description",
      renderCell: ({ value }) => <RenderWithTooltip value={value || "-"} />,
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 120,
      renderCell: ({ row }: any) => (
        <ActionMenu
          options={[
            { value: "edit", label: "Edit" },
            row?.archive
              ? { value: "unarchive", label: "Unarchive" }
              : { value: "archive", label: "Archive" },
          ]}
          onActionClick={(action) => handleActionClick(action, row)}
        />
      ),
    },
  ]);

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(!archive, id).then((res) => {
            refetch();
            return res;
          })
        }
        content={`You want to ${
          archive ? "unarchive" : "archive"
        } this Contact`}
      />

      {/* Add/Edit New Contact Form */}
      <UpsertContactDictionary disclosureHost={contactDictionaryDisclosure} />

      <Box flex={1} overflow="auto">
        <CustomDataGridV2
          loading={isFetching || isLoading}
          rows={contactDictionary?.data.content || []}
          rowCount={contactDictionary?.data?.page?.totalElements ?? 10}
          getRowId={(row) => row.uuid}
          columns={COLUMNS}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
        />
      </Box>
    </Box>
  );
};

export default ContactDictionary;
